if (!customElements.get('wlsn-buybox')) {
  class WLSNBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        buyboxRoot: 'wlsn-buybox',
      };

      this.bindMethods = {
        onOptionChange: this._onOptionChange.bind(this),
        onPopState: this._onPopState.bind(this),
        onPageUpdated: this._onPageUpdated.bind(this),
      };

      this.isProductPage =
        this.getAttribute('data-is-product-page') === 'true' && window.location.pathname.startsWith('/products/');

      this.templateName = this.getAttribute('data-template-name') || '';
      
      this.isUpdating = false;
    }

    /**
     * Get elements
     * @returns {Object} - Object containing DOM element references
     */
    get elements() {
      return {};
    }

    /**
     * Connected callback method
     * @returns {void}
     */
    connectedCallback() {
      this._attachEventListeners();
      // Handle browser back/forward navigation
      window.addEventListener('popstate', this.bindMethods.onPopState);
      // Listen for page updates to reattach listeners
      window.addEventListener('wlsn:page:updated', this.bindMethods.onPageUpdated);
    }

    /**
     * Disconnected callback method
     * @returns {void}
     */
    disconnectedCallback() {
      this._detachEventListeners();
      window.removeEventListener('popstate', this.bindMethods.onPopState);
      window.removeEventListener('wlsn:page:updated', this.bindMethods.onPageUpdated);
    }

    /**
     * Attach event listeners
     * @returns {void}
     */
    _attachEventListeners() {}

    /**
     * Detach event listeners
     * @returns {void}
     */
    _detachEventListeners() {}

    /**
     * Handle page updated event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onPageUpdated(event) {
      // Re-attach event listeners after page update
      this._detachEventListeners();
      this._attachEventListeners();
    }

    /**
     * Handle option change event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onOptionChange(event) {
      if (event.detail && event.detail.url) {
        this.isProductPage ? this._updateProductPage(event.detail.url) : this._updateProductContent(event.detail.url);
      }
    }

    /**
     * Handle pop state event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onPopState(event) {
      // Handle browser back/forward navigation
      this._updateProductPage(window.location.href, false);
    }

    /**
     * Update product page
     * @param {string} url - URL to update
     * @param {boolean} updateHistory - Whether to update history
     * @returns {void}
     */
    async _updateProductPage(url, updateHistory = true) {
      if (this.isUpdating) return;

      try {
        this.isUpdating = true;

        // Fetch the new product page
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch product page');

        const html = await response.text();

        // Parse the HTML
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        // Find and update ALL Shopify sections in main
        const currentMain = document.querySelector('main');
        const newMain = newDocument.querySelector('main');

        if (!currentMain || !newMain) {
          throw new Error('Could not find main content container');
        }

        const currentSections = currentMain.querySelectorAll('[id^="shopify-section-"]');
        let replacedCount = 0;

        currentSections.forEach((currentSection) => {
          const sectionId = currentSection.id;
          if (!sectionId) return;

          // CSS.escape for weird id's
          const newSection = newMain.querySelector(`#${CSS.escape(sectionId)}`);

          if (newSection) {
            currentSection.innerHTML = newSection.innerHTML;
            this._executeScripts(currentSection);
            replacedCount++;
          }
        });

        // No sections were updated → very likely another template
        if (replacedCount === 0) {
          // Revert to "native" behavior – full navigation
          window.location.href = url;
          return;
        }

        // SPA scenario – successfully replaced sections
        if (updateHistory) {
          window.history.pushState({ path: url }, '', url);
        }

        window.dispatchEvent(new CustomEvent('wlsn:page:updated', { detail: { url } }));
      } catch (error) {
        console.error('Error updating product page:', error);
        window.location.href = url;
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * Update product content
     * @param {string} url - URL to update
     * @returns {void}
     */
    async _updateProductContent(url) {
      if (this.isUpdating) return;

      // If no template name is set or it's "none", skip the update
      if (!this.templateName || this.templateName === 'none') {
        return;
      }

      try {
        this.isUpdating = true;

        const response = await fetch(`${url}?view=${this.templateName}`);
        if (!response.ok) throw new Error('Failed to fetch product page');

        const html = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        const currentSections = document.querySelectorAll('[id^="shopify-section-"]');
        const newSections = newDocument.querySelectorAll('[id^="shopify-section-"]');

        // Create a map of current sections by section key
        const currentByKey = new Map();
        currentSections.forEach((section) => {
          const key = this._getSectionKeyFromId(section.id);
          if (!key) return;
          currentByKey.set(key, section);
        });

        // Loop through new sections and find pairs by key
        newSections.forEach((newSection) => {
          const key = this._getSectionKeyFromId(newSection.id);
          if (!key) return;

          const currentSection = currentByKey.get(key);
          if (!currentSection) return;

          currentSection.replaceWith(newSection);
          this._executeScripts(newSection);
        });

        window.dispatchEvent(
          new CustomEvent('wlsn:page:updated', {
            detail: { url: `${url}?view=${this.templateName}` },
          })
        );
      } catch (error) {
        console.error('Error updating product page:', error);
        window.location.href = url;
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * Get section key from id
     * @param {string} sectionId - Section id
     * @returns {string} - Section key
     */
    _getSectionKeyFromId(sectionId) {
      // Expected format:
      // shopify-section-template--25368737087808__wlsn_buybox_TGRqEC
      // shopify-section-template--25368737349952__wlsn_before_after_Dkc8QC

      const match = sectionId.match(/__(.+)_([A-Za-z0-9]+)$/);
      //             ^   ^    ^
      //             |   |    └ random suffix (e.g. TGRqEC)
      //             |   └ SECTION_KEY (can contain _ )
      //             └ everything after "__"

      return match ? match[1] : null; // e.g. "wlsn_buybox" or "wlsn_before_after"
    }

    /**
     * Execute scripts in container element
     * @param {Element} container - Container element
     * @returns {void}
     */
    _executeScripts(container) {
      // Find all script tags in the container
      const scripts = container.querySelectorAll('script');

      scripts.forEach((oldScript) => {
        // Create a new script element
        const newScript = document.createElement('script');

        // Copy all attributes
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Copy the script content
        newScript.textContent = oldScript.textContent;

        // Replace the old script with the new one to trigger execution
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  }
  customElements.define('wlsn-buybox', WLSNBuybox);
}
